import { useState, useEffect } from 'react';
import { Modal, Box, Button, Typography, Switch, IconButton, FormControlLabel, Snackbar } from '@mui/material';
import './../MaintenanceBanner/MaintenanceBanner.css';
import { getEoriValidationSettings, putEoriValidationSettings } from '../../api/settings';

const validationSwitchSx = {
  '& .MuiSwitch-switchBase': {
    color: '#d32f2f', // thumb when disabled (red)
  },
  '& .MuiSwitch-switchBase + .MuiSwitch-track': {
    backgroundColor: '#d32f2f', // track when disabled (red)
    opacity: 0.5,
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#2e7d32', // thumb when enabled (green)
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#2e7d32', // track when enabled (green)
    opacity: 0.5,
  },
  '& .MuiSwitch-switchBase.Mui-checked:hover, & .MuiSwitch-switchBase:hover': {
    backgroundColor: 'transparent',
  },
};

const EoriValidationsModal = ({ show, onHide }) => {
  const [euValidationEnabled, setEuValidationEnabled] = useState(true);
  const [gbValidationEnabled, setGbValidationEnabled] = useState(true);
  const [lastModified, setLastModified] = useState({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarBackgroundColor, setSnackbarBackgroundColor] = useState('red');

  const showSnackbar = (message, backgroundColor = 'red') => {
    setSnackbarMessage(message);
    setSnackbarBackgroundColor(backgroundColor);
    setSnackbarOpen(true);
  };

  const hideSnackbar = () => {
    setSnackbarOpen(false);
  };

  const fetchData = async () => {
    const data = await getEoriValidationSettings();
    if (data) {
      const { euEoriValidation, gbEoriValidation } = data;
      setEuValidationEnabled(euEoriValidation);
      setGbValidationEnabled(gbEoriValidation);
      setLastModified({
        user: data.lastModifiedUser || '',
        date: data.lastModified || '',
      });
    }
  };

  const onSave = async () => {
    try {
      await putEoriValidationSettings({
        euEoriValidation: euValidationEnabled,
        gbEoriValidation: gbValidationEnabled,
        lastModifiedUser: sessionStorage.getItem('name') || '',
      });
      showSnackbar('Validation details saved successfully', 'green');
    } catch (error) {
      console.log('Error saving validation details:', error);
      showSnackbar('Error saving validation details', 'red');
    } finally {
      onHide();
    }
  };

  useEffect(() => {
    if (show) fetchData();
  }, [show]);

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        onClose={hideSnackbar}
        message={snackbarMessage}
        autoHideDuration={4000}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarBackgroundColor,
            color: 'white',
          },
          mt: 9,
        }}
      />
      <Modal
        open={show}
        onClose={() => {
          onHide();
        }}
        className="maintenance-banner-modal"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '84%',
            maxWidth: 820,
            bgcolor: '#f5f5f5',
            border: '1px solid #d2d2d2',
            borderRadius: 1,
            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
            p: 3,
            outline: 'none',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography sx={{ fontSize: '1.6rem', color: '#1f1f1f', fontWeight: 400 }}>
              EORI Number Validation
            </Typography>

            <IconButton
              onClick={() => {
                onHide();
              }}
              aria-label="close"
            >
              <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>x</Typography>
            </IconButton>
          </Box>

          <Box sx={{ borderTop: '1px solid #bfbfbf', width: '100%' }} />

          <Box sx={{ pt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: '1rem', color: '#1f1f1f' }}>EU EORI Number Validation</Typography>

              <Box className="maintenance-details-enabled" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" className="maintenance-subheading" sx={{ mb: 1 }}>
                  Enabled / Disabled
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      name="enabled"
                      checked={euValidationEnabled}
                      onChange={(e) => setEuValidationEnabled(e.target.checked)}
                      sx={validationSwitchSx}
                    />
                  }
                  label={euValidationEnabled ? 'Enabled' : 'Disabled'}
                />
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #d3d3d3', width: '100%' }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
              }}
            >
              <Typography sx={{ fontSize: '1rem', color: '#1f1f1f' }}>GB EORI Number Validation</Typography>

              <Box className="maintenance-details-enabled" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" className="maintenance-subheading" sx={{ mb: 1 }}>
                  Enabled / Disabled
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      name="enabled"
                      checked={gbValidationEnabled}
                      onChange={(e) => setGbValidationEnabled(e.target.checked)}
                      sx={validationSwitchSx}
                    />
                  }
                  label={gbValidationEnabled ? 'Enabled' : 'Disabled'}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ borderTop: '1px solid #d3d3d3', width: '100%', mt: 2 }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2.5,
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: '0.9rem', color: '#4b4b4b' }}>
              Last updated by:{' '}
              {lastModified.user && lastModified.date
                ? `${lastModified.user} on ${new Date(lastModified.date).toLocaleString()}`
                : '--'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  onHide();
                }}
                sx={{
                  minWidth: 110,
                  borderColor: '#2e7ed1',
                  color: '#2e7ed1',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  borderRadius: 1,
                  px: 2,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  minWidth: 110,
                  bgcolor: '#2d7fe7',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  borderRadius: 1,
                  px: 2,
                  '&:hover': { bgcolor: '#246ed1' },
                }}
                onClick={onSave}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default EoriValidationsModal;
